import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, catchError, finalize, tap } from 'rxjs';
import { ContactDeliveryAddressFormModel } from 'src/app/core/models/contact/contact-delivery-address-form.model';
import { ContactDeliveryAddress } from 'src/app/core/models/contact/contact-delivery-address.interface';
import { ContactDeliveryCarrier } from 'src/app/core/models/contact/contact-delivery-carrier.type';
import { ContactDeliveryAddressKind } from 'src/app/core/models/contact/contact-delivery-address-kind.type';
import { ToastsContainerComponent } from 'src/app/core/components/toast/toasts-container.component';
import { ContactService } from 'src/app/core/services/contact.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { ContactDeliveryAddressCardViewModel } from './contact-delivery-address-card-view-model.interface';
import {
  buildDeliveryAddressCards,
  cloneDeliveryAddresses,
  mapFormValueToDeliveryAddress,
  normalizeDeliveryAddresses,
} from './contact-delivery-addresses-section.helpers';

type EditorMode = 'create' | 'edit' | null;

@Component({
  selector: 'app-contact-delivery-addresses-section',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, ToastsContainerComponent],
  templateUrl: './contact-delivery-addresses-section.component.html',
  styleUrl: './contact-delivery-addresses-section.component.scss',
  providers: [ContactDeliveryAddressFormModel],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactDeliveryAddressesSectionComponent {
  readonly addressForm = inject(ContactDeliveryAddressFormModel);
  private readonly contactService = inject(ContactService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);

  @ViewChild('dangerTpl', { static: true }) private readonly dangerTpl?: TemplateRef<unknown>;
  @Input({ required: true }) public contactId = '';
  @Input()
  public set deliveryAddresses(value: ContactDeliveryAddress[]) {
    this.currentDeliveryAddresses = cloneDeliveryAddresses(value ?? []);
    this.refreshDeliveryAddressCards();
  }

  public get deliveryAddresses(): ContactDeliveryAddress[] {
    return cloneDeliveryAddresses(this.currentDeliveryAddresses);
  }

  @Input() public editable = false;

  @Output() public deliveryAddressesChange = new EventEmitter<ContactDeliveryAddress[]>();

  public isSaving = false;
  public callbackError = '';
  public editorMode: EditorMode = null;
  public editingAddressId: string | null = null;
  public editingKind: ContactDeliveryAddressKind = 'branch';
  public editingCarrier: ContactDeliveryCarrier | null = 'nova-poshta';
  public deliveryAddressCards: ContactDeliveryAddressCardViewModel[] = [];

  public readonly carrierOptions: Array<{
    value: ContactDeliveryCarrier;
    labelKey: string;
  }> = [
    {
      value: 'nova-poshta',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.CARRIERS.NOVA_POSHTA',
    },
    {
      value: 'ukrposhta',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.CARRIERS.UKRPOSHTA',
    },
    {
      value: 'meest',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.CARRIERS.MEEST',
    },
    {
      value: 'other',
      labelKey: 'COMPONENTS.CONTACT_OVERVIEW.DELIVERY_ADDRESSES.CARRIERS.OTHER',
    },
  ];

  private currentDeliveryAddresses: ContactDeliveryAddress[] = [];

  public startCreateAddress(): void {
    this.editorMode = 'create';
    this.editingAddressId = this.generateAddressId();
    this.editingKind = 'branch';
    this.editingCarrier = 'nova-poshta';
    this.addressForm.reset({
      kind: 'branch',
      isPrimary: this.currentDeliveryAddresses.length === 0,
      carrier: 'nova-poshta',
    });
  }

  public startEditAddress(addressId: string): void {
    const address = this.currentDeliveryAddresses.find((item) => item.id === addressId);
    if (!address) {
      return;
    }

    this.editorMode = 'edit';
    this.editingAddressId = addressId;
    this.editingKind = address.kind;
    this.editingCarrier = address.carrier ?? 'nova-poshta';
    this.addressForm.reset(address);
  }

  public cancelEditing(): void {
    this.editorMode = null;
    this.editingAddressId = null;
    this.editingKind = 'branch';
    this.editingCarrier = 'nova-poshta';
    this.addressForm.reset();
  }

  public saveAddress(): void {
    if (!this.editingAddressId) {
      return;
    }

    if (this.addressForm.form.invalid) {
      this.addressForm.form.markAllAsTouched();
      return;
    }

    const nextAddress = this.mapFormToAddress(this.editingAddressId);
    const pendingAddresses = this.editorMode === 'edit'
      ? this.currentDeliveryAddresses.map((address) =>
          address.id === this.editingAddressId ? nextAddress : address)
      : [...this.currentDeliveryAddresses, nextAddress];

    const nextAddresses = nextAddress.isPrimary
      ? pendingAddresses.map((address) => ({
          ...address,
          isPrimary: address.id === nextAddress.id,
        }))
      : pendingAddresses;

    this.persistDeliveryAddresses(nextAddresses);
  }

  public removeAddress(addressId: string): void {
    const nextAddresses = this.currentDeliveryAddresses.filter(
      (address) => address.id !== addressId,
    );
    this.persistDeliveryAddresses(nextAddresses);
  }

  public setPrimaryAddress(addressId: string): void {
    const nextAddresses = this.currentDeliveryAddresses.map((address) => ({
      ...address,
      isPrimary: address.id === addressId,
    }));
    this.persistDeliveryAddresses(nextAddresses);
  }

  public selectKind(kind: ContactDeliveryAddressKind): void {
    this.editingKind = kind;
    this.addressForm.form.controls.kind.setValue(kind);

    if (kind === 'address') {
      this.editingCarrier = null;
      this.addressForm.form.patchValue({
        carrier: null,
        carrierName: null,
        branchCity: null,
        branchNumber: null,
      });
      return;
    }

    const nextCarrier = this.addressForm.form.controls.carrier.value ?? 'nova-poshta';
    this.editingCarrier = nextCarrier;
    this.addressForm.form.patchValue({
      country: null,
      city: null,
      street: null,
      postalCode: null,
      carrier: nextCarrier,
    });
  }

  public selectCarrier(carrier: ContactDeliveryCarrier | null): void {
    this.editingCarrier = carrier;
    this.addressForm.form.controls.carrier.setValue(carrier);

    if (carrier !== 'other') {
      this.addressForm.form.controls.carrierName.setValue(null);
    }
  }

  public toggleEditingPrimary(): void {
    const control = this.addressForm.form.controls.isPrimary;
    control.setValue(!control.value);
  }

  private persistDeliveryAddresses(nextAddresses: ContactDeliveryAddress[]): void {
    if (!this.contactId) {
      return;
    }

    const normalizedAddresses = normalizeDeliveryAddresses(nextAddresses);
    this.isSaving = true;
    this.callbackError = '';

    this.contactService
      .updateDeliveryAddresses(this.contactId, normalizedAddresses)
      .pipe(
        tap((savedAddresses) => {
          const persistedAddresses = cloneDeliveryAddresses(savedAddresses);
          this.currentDeliveryAddresses = persistedAddresses;
          this.deliveryAddressesChange.emit(cloneDeliveryAddresses(persistedAddresses));
          this.cancelEditing();
          this.refreshDeliveryAddressCards();
        }),
        catchError(() => {
          this.callbackError = this.translateService.instant('ERRORS.UNKNOWN_ERROR');
          if (this.dangerTpl) {
            this.toastService.show({
              template: this.dangerTpl,
              className: 'toast-danger',
            });
          }
          return EMPTY;
        }),
        finalize(() => {
          this.isSaving = false;
        }),
      )
      .subscribe();
  }

  private mapFormToAddress(addressId: string): ContactDeliveryAddress {
    return mapFormValueToDeliveryAddress(this.addressForm.form.getRawValue(), addressId);
  }

  private refreshDeliveryAddressCards(): void {
    this.deliveryAddressCards = buildDeliveryAddressCards(this.currentDeliveryAddresses);
  }

  private generateAddressId(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const randomValue = Math.floor(Math.random() * 16);
      const normalizedValue = character === 'x' ? randomValue : (randomValue & 0x3) | 0x8;
      return normalizedValue.toString(16);
    });
  }
}
